// Okta uses the he classic anchor-as-button pattern (WCAG 4.1.2)

import type { IdxTransactionManagerInterface } from '@okta/okta-auth-js';

// Ensure anchor buttons include the button role and activate on Space and Enter.
const ACTIVATION_KEYS = new Set([' ', 'Spacebar', 'Enter']);
const LINK_BUTTON_SELECTOR =
  '.link-button:not(.button-primary), .js-switchAuthenticator, .js-cancel, .switch-channel-link';
const LINK_BUTTON_PATCHED_ATTR = 'data-a11y-role-button-patched';

export const enhanceLinkButtonRoles = (container: HTMLElement): void => {
  container
    .querySelectorAll<HTMLAnchorElement>(LINK_BUTTON_SELECTOR)
    .forEach((link) => {
      if (link.hasAttribute(LINK_BUTTON_PATCHED_ATTR)) return;

      link.setAttribute('role', 'button');
      link.setAttribute(LINK_BUTTON_PATCHED_ATTR, 'true');
      link.addEventListener('keydown', (event) => {
        if (ACTIVATION_KEYS.has(event.key)) event.preventDefault();
      });
      link.addEventListener('keyup', (event) => {
        if (ACTIVATION_KEYS.has(event.key)) {
          event.preventDefault();
          link.click();
        }
      });
    });
};

// WCAG 1.3.1 - add grouping/list cues to the okta verify options
const AUTHENTICATOR_LIST_SELECTOR = '.authenticator-verify-list';
const AUTHENTICATOR_ROW_SELECTOR = `${AUTHENTICATOR_LIST_SELECTOR} .authenticator-row`;
const AUTHENTICATOR_LIST_PATCHED_ATTR = 'data-a11y-list-role-patched';

export const enhanceAuthenticatorListSemantics = (
  container: HTMLElement
): void => {
  container
    .querySelectorAll<HTMLElement>(AUTHENTICATOR_LIST_SELECTOR)
    .forEach((list) => {
      if (list.hasAttribute(AUTHENTICATOR_LIST_PATCHED_ATTR)) return;

      list.setAttribute('role', 'list');
      list.setAttribute(AUTHENTICATOR_LIST_PATCHED_ATTR, 'true');
    });

  container
    .querySelectorAll<HTMLElement>(AUTHENTICATOR_ROW_SELECTOR)
    .forEach((row) => {
      if (row.hasAttribute(AUTHENTICATOR_LIST_PATCHED_ATTR)) return;

      row.setAttribute('role', 'listitem');
      row.setAttribute(AUTHENTICATOR_LIST_PATCHED_ATTR, 'true');
    });
};

// WCAG 4.1.2 - Ensure screenreader users are notified the "Push notification sent" button is disabled
const SEND_PUSH_SELECTOR = '.send-push';

export const enhanceSendPushDisabledState = (container: HTMLElement): void => {
  container
    .querySelectorAll<HTMLAnchorElement>(SEND_PUSH_SELECTOR)
    .forEach((button) => {
      button.setAttribute('role', 'button');

      if (button.classList.contains('link-button-disabled')) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    });
};

const EXPIRY_SAFETY_MARGIN_MS = 30000;

export const isIdxTransactionExpired = (
  transactionManager: Pick<IdxTransactionManagerInterface, 'loadIdxResponse'>
): boolean => {
  const savedResponse = transactionManager.loadIdxResponse();

  if (!savedResponse?.rawIdxResponse) {
    return false;
  }

  const { expiresAt } = savedResponse.rawIdxResponse;

  if (!expiresAt) return false;

  const expiresAtMs = new Date(expiresAt).getTime();

  return Date.now() >= expiresAtMs - EXPIRY_SAFETY_MARGIN_MS;
};
